import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Users, FileText, DollarSign, AlertCircle, TrendingUp, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

export default function AgencyPortal() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAthletes: 0,
    activeDeals: 0,
    totalValue: 0,
    pendingReviews: 0,
    activeCampaigns: 0,
    commission: 0,
  });
  const [organization, setOrganization] = useState<any>(null);

  useEffect(() => {
    loadAgencyData();
  }, []);

  const loadAgencyData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's organization
      const { data: membership } = await supabase
        .from("organization_members")
        .select("*, organizations(*)")
        .eq("user_id", user.id)
        .single();

      if (!membership?.organizations) {
        toast({
          title: "No Organization",
          description: "You are not a member of any agency.",
          variant: "destructive",
        });
        navigate("/dashboard");
        return;
      }

      setOrganization(membership.organizations);

      // Get organization athletes count
      const { count: athleteCount } = await supabase
        .from("organization_athletes")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", membership.organizations.id)
        .eq("status", "active");

      // Get active deals count and total value
      const { data: deals } = await supabase
        .from("deals")
        .select("amount, status")
        .eq("organization_id", membership.organizations.id);

      const activeDeals = deals?.filter(d => d.status === "active") || [];
      const totalValue = activeDeals.reduce((sum, d) => sum + Number(d.amount), 0);

      // Get pending compliance reviews
      const { count: pendingCount } = await supabase
        .from("deals")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", membership.organizations.id)
        .eq("status", "pending_compliance");

      // Get active campaigns
      const { count: campaignCount } = await supabase
        .from("campaigns")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", membership.organizations.id)
        .eq("status", "active");

      // Calculate commission (assuming average 15%)
      const commission = totalValue * 0.15;

      setStats({
        totalAthletes: athleteCount || 0,
        activeDeals: activeDeals.length,
        totalValue,
        pendingReviews: pendingCount || 0,
        activeCampaigns: campaignCount || 0,
        commission,
      });
    } catch (error) {
      console.error("Error loading agency data:", error);
      toast({
        title: "Error",
        description: "Failed to load agency dashboard",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-muted-foreground">Loading agency dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{organization?.name}</h1>
          <p className="text-muted-foreground">Agency Dashboard</p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Athletes</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalAthletes}</div>
              <p className="text-xs text-muted-foreground">Active roster</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeDeals}</div>
              <p className="text-xs text-muted-foreground">Current partnerships</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Deal Value</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats.totalValue.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Active deals</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Est. Commission</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats.commission.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">15% average</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pendingReviews}</div>
              <p className="text-xs text-muted-foreground">Awaiting compliance</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Campaigns</CardTitle>
              <Briefcase className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeCampaigns}</div>
              <p className="text-xs text-muted-foreground">Running campaigns</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks for agency management</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-4">
            <Button onClick={() => navigate("/agency/athletes")}>
              <Users className="mr-2 h-4 w-4" />
              Manage Athletes
            </Button>
            <Button onClick={() => navigate("/agency/campaigns")} variant="outline">
              <Briefcase className="mr-2 h-4 w-4" />
              Create Campaign
            </Button>
            <Button onClick={() => navigate("/agency/deals")} variant="outline">
              <FileText className="mr-2 h-4 w-4" />
              View Deals
            </Button>
            <Button onClick={() => navigate("/agency/analytics")} variant="outline">
              <TrendingUp className="mr-2 h-4 w-4" />
              Analytics
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
